import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import {
  Landmark,
  Building2,
  Coins,
  Banknote,
  Briefcase,
  ShieldHalf,
  Globe,
  CircuitBoard,
  ArrowUpRight,
} from "lucide-react";

const SOLUTIONS = [
  {
    icon: Landmark,
    name: "Banks",
    headline: "Institutional-grade custody for digital assets.",
    body:
      "Secure digital asset custody with identity-linked governance, auditability, and asset segregation — built to satisfy regulators and risk committees.",
  },
  {
    icon: Building2,
    name: "Exchanges",
    headline: "Reduce hot wallet and pooled custody risk.",
    body:
      "Hardware-segregated, policy-enforced transaction control eliminates impersonation risk and contains the blast radius of any single compromise.",
  },
  {
    icon: Coins,
    name: "Tokenization Platforms",
    headline: "Lifecycle control for real-world assets.",
    body:
      "Compliant custody with hardware-enforced lifecycle rules — issuance, transfer restrictions, beneficial ownership, redemption, and audit.",
  },
  {
    icon: Banknote,
    name: "Stablecoin Issuers",
    headline: "Provable segregation. Identity-linked authority.",
    body:
      "Identity-linked transaction control and institutional-grade asset protection for mint, burn, treasury, and reserves.",
  },
  {
    icon: Briefcase,
    name: "Asset Managers",
    headline: "Multi-party governance for client assets.",
    body:
      "Auditable, multi-party approvals and secure operational workflows — protect client capital with hardware and policy.",
  },
  {
    icon: ShieldHalf,
    name: "Custodians",
    headline: "A differentiated, hardware-grade offering.",
    body:
      "Per-user hardware segregation, biometric authority, and full audit trails for regulated digital asset custodianship.",
  },
  {
    icon: Globe,
    name: "Governments",
    headline: "Sovereign-grade digital ownership.",
    body:
      "Biometric-secured digital identity, wallet access, and policy-controlled public digital finance infrastructure.",
  },
  {
    icon: CircuitBoard,
    name: "CBDCs",
    headline: "Citizen-grade identity bound to currency.",
    body:
      "Identity-bound wallets, policy-governed disbursement, and sovereign-scale auditability for central bank digital currency.",
  },
];

export default function Solutions() {
  return (
    <main className="bg-white" data-testid="solutions-page">
      <section className="pt-32 md:pt-40 pb-16 bg-radial-soft relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-50 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-8 relative">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
              Solutions
            </p>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl font-medium leading-[1.02] tracking-tight text-[#06102A] max-w-4xl">
              Infrastructure for every regulated digital asset model.
            </h1>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-7 max-w-2xl text-lg text-[#06102A]/70">
              From central banks to exchanges, Vaultavo configures to the operating model of
              regulated digital finance — not the other way around.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-24 md:py-32" data-testid="solutions-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-8 grid grid-cols-1 md:grid-cols-2 gap-5">
          {SOLUTIONS.map((s, i) => (
            <Reveal key={s.name} delay={(i % 2) * 100}>
              <article className="group relative rounded-3xl border border-[#06102A]/10 bg-white p-8 md:p-10 hover:border-[#06102A] hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(6,16,42,0.25)] transition-all duration-500">
                <div className="flex items-start justify-between">
                  <div className="h-12 w-12 rounded-xl bg-[#06102A]/[0.04] grid place-items-center">
                    <s.icon className="h-5 w-5 text-[#06102A]" strokeWidth={1.6} />
                  </div>
                  <span className="font-mono-vt text-xs text-[#06102A]/45">
                    {String(i + 1).padStart(2, "0")} / 08
                  </span>
                </div>
                <h3 className="mt-8 font-display text-2xl md:text-3xl font-medium text-[#06102A]">
                  {s.name}
                </h3>
                <p className="mt-2 text-[#06102A]/85 text-[15px]">{s.headline}</p>
                <p className="mt-4 text-sm text-[#06102A]/70 leading-relaxed">{s.body}</p>
                <Link
                  to="/contact"
                  className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-[#06102A] vt-link"
                  data-testid={`solution-cta-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  Discuss a deployment
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </article>
            </Reveal>
          ))}
        </div>
      </section>
    </main>
  );
}
